'use client';
// src/components/LegalLinks.jsx
//
// Compact row of legal links (Privacy · Terms · SMS Policy). Drop into
// page footers, sidebars, or anywhere the user might need to find legal
// info. Two visual variants:
//
//   <LegalLinks />          — neutral, fits dark + light backgrounds
//   <LegalLinks variant="onLime" />  — for use over a lime accent surface
//
// All links open in a new tab (rel=noopener noreferrer) since they live
// on the marketing domain, not the app.

import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { LEGAL_LINKS } from '@/constants/legal';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

export default function LegalLinks({
  className = '',
  variant = 'default', // 'default' | 'subtle' | 'onLime'
  separator = '·',
}) {
  const { i18n } = useTranslation();
  const esUi = String(i18n?.language || 'en').toLowerCase().startsWith('es');
  const linkClass = clsx(
    'transition-colors',
    variant === 'onLime'   && 'text-[#0d1f14]/70 hover:text-[#0d1f14] underline-offset-2 hover:underline',
    variant === 'subtle'   && 'text-zinc-400 hover:text-[#5a8a1f]',
    variant === 'default'  && 'text-zinc-500 hover:text-[#5a8a1f]',
  );
  const sepClass = clsx(
    variant === 'onLime'  && 'text-[#0d1f14]/30',
    variant === 'subtle'  && 'text-zinc-300',
    variant === 'default' && 'text-zinc-300',
  );

  return (
    <nav
      aria-label="Legal links"
      className={clsx(
        'flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.1em]',
        className
      )}
      style={{ fontFamily: HEAD_FONT }}
    >
      {LEGAL_LINKS.map((link, i) => (
        <React.Fragment key={link.href}>
          {i > 0 && <span aria-hidden="true" className={sepClass}>{separator}</span>}
          <a
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            {esUi ? (link.labelEs || link.label) : link.label}
          </a>
        </React.Fragment>
      ))}
    </nav>
  );
}
